import { ApiError } from '../api/_core'
import {
    meta as fetchMeta,
    browse,
    unlockPhone as unlockPhoneRequest,
    call as callRequest,
    favorite as favoriteRequest,
    note as noteRequest,
    report as reportRequest,
    review as reviewRequest,
    updatesMore
} from '../api/escort-ads'
import { toParams, filtersKey, PAGE_LIMIT } from '../components/escort-ads/util'

const ACTION_ACCESS = 'ESCORT_ADS/ACCESS',
    ACTION_MENU_TOTAL = 'ESCORT_ADS/MENU_TOTAL',
    ACTION_RECEIVE_ADS = 'ESCORT_ADS/RECEIVE_ADS',
    ACTION_PATCH_AD = 'ESCORT_ADS/PATCH_AD',
    ACTION_PHONE_DIALOG = 'ESCORT_ADS/PHONE_DIALOG',
    ACTION_PHONE_LOADING = 'ESCORT_ADS/PHONE_LOADING',
    ACTION_LIST_REQUEST = 'ESCORT_ADS/LIST_REQUEST',
    ACTION_LIST_SUCCESS = 'ESCORT_ADS/LIST_SUCCESS',
    ACTION_LIST_FAILURE = 'ESCORT_ADS/LIST_FAILURE',
    ACTION_LIST_SCROLL = 'ESCORT_ADS/LIST_SCROLL',
    ACTION_HISTORY_TOTAL = 'ESCORT_ADS/HISTORY_TOTAL',
    ACTION_PHONE_UNLOCKED = 'ESCORT_ADS/PHONE_UNLOCKED',
    ACTION_HISTORY_DIALOG = 'ESCORT_ADS/HISTORY_DIALOG';

export const ACCESS_LOADING = 'loading'
export const ACCESS_GRANTED = 'granted'
export const ACCESS_DENIED = 'denied'

const initState = {
    // null means "not checked yet" (or the last check failed and can be retried)
    access: null,
    meta: null,
    menuTotal: null,
    // Every loaded ad by id. All views read from here, so one update syncs every copy.
    ads: {},
    phoneDialog: null,
    phoneLoading: {},
    // The last unlocked phone. It unlocks every ad with the same number, so the ad pages refresh their phone section.
    unlocked: null,
    // The list page, kept so going back from the detail restores it without a new request.
    list: null,
    // Last known number of phones per list (by filters key), for the counts of the list tabs.
    totals: {},
    // Visited and called ads of the user, for the badge of the history button.
    historyTotal: 0,
    // The open tab of the history dialog ('views' or 'calls'), null when it is closed.
    historyDialog: null
}

export default function (state = initState, { type, payload }) {
    switch (type) {
        case ACTION_ACCESS:
            return {
                ...state,
                access: payload.access,
                meta: payload.meta || state.meta
            }
        case ACTION_MENU_TOTAL:
            return {
                ...state,
                menuTotal: payload
            }
        case ACTION_RECEIVE_ADS: {
            const ads = { ...state.ads }
            payload.forEach(ad=>{
                ads[ad.id] = { ...ads[ad.id], ...ad }
            })
            return {
                ...state,
                ads
            }
        }
        case ACTION_PATCH_AD: {
            if(!state.ads[payload.id]) {
                return state
            }
            // A changed favorite makes the known totals of the favorites lists stale.
            let totals = state.totals
            if('is_favorite' in payload.patch) {
                totals = {}
                Object.keys(state.totals).forEach(key=>{
                    if(key.split('&').indexOf('favorites=1') === -1) {
                        totals[key] = state.totals[key]
                    }
                })
            }
            return {
                ...state,
                totals,
                ads: {
                    ...state.ads,
                    [payload.id]: {
                        ...state.ads[payload.id],
                        ...payload.patch
                    }
                }
            }
        }
        case ACTION_PHONE_DIALOG:
            return {
                ...state,
                phoneDialog: payload
            }
        case ACTION_PHONE_LOADING:
            return {
                ...state,
                phoneLoading: {
                    ...state.phoneLoading,
                    [payload.id]: payload.loading
                }
            }
        case ACTION_LIST_REQUEST:
            if(payload.reset) {
                return {
                    ...state,
                    list: {
                        key: payload.key,
                        // The filters the server used: without city and order in the URL it picks them by the location of the user.
                        filters: null,
                        ids: [],
                        total: 0,
                        totalPhones: 0,
                        offset: 0,
                        hasMore: false,
                        loading: true,
                        loaded: false,
                        scrollY: 0,
                        requestId: payload.requestId
                    }
                }
            }
            return {
                ...state,
                list: {
                    ...state.list,
                    loading: true,
                    requestId: payload.requestId
                }
            }
        case ACTION_LIST_SUCCESS: {
            const ids = payload.offset === 0 ? [] : state.list.ids.concat()
            payload.ids.forEach(id=>{
                if(ids.indexOf(id) === -1) {
                    ids.push(id)
                }
            })
            return {
                ...state,
                list: {
                    ...state.list,
                    ids,
                    filters: payload.filters || state.list.filters,
                    total: payload.total,
                    totalPhones: payload.totalPhones,
                    offset: payload.offset,
                    hasMore: payload.ids.length > 0 && payload.offset + payload.ids.length < payload.total,
                    loading: false,
                    loaded: true
                },
                totals: {
                    ...state.totals,
                    [state.list.key]: payload.totalPhones
                }
            }
        }
        case ACTION_LIST_FAILURE:
            return {
                ...state,
                list: {
                    ...state.list,
                    loading: false,
                    loaded: true
                }
            }
        case ACTION_LIST_SCROLL:
            if(!state.list) {
                return state
            }
            return {
                ...state,
                list: {
                    ...state.list,
                    scrollY: payload
                }
            }
        case ACTION_PHONE_UNLOCKED:
            return {
                ...state,
                unlocked: payload
            }
        case ACTION_HISTORY_DIALOG:
            return {
                ...state,
                historyDialog: payload
            }
        case ACTION_HISTORY_TOTAL:
            return {
                ...state,
                historyTotal: payload
            }
        default:
            return state
    }
}

export function isNotFound(e) {
    return e instanceof ApiError && (e.code === 404 || e.status === 404)
}

export function isEscortEnabled(state) {
    const user = state.app.user
    return state.escortAds.access === ACCESS_GRANTED && user && Number(user.sex.value) === 2 ? true : false
}

// The unique phones of all public ads, the same number as the menu counter. null until it is loaded.
export function getEscortTotal(state) {
    const data = state.updates.data
    return data && data.escort_ads ? Number(data.escort_ads.total) || 0 : state.escortAds.menuTotal
}

// The module has no flag on desktop: the meta endpoint answers 404 when it is disabled for the user.
export function loadAccess() {
    return async (dispatch, getState) => {
        const { app: { user }, escortAds: { access } } = getState()
        if(access !== null) {
            return
        }
        if(!user || Number(user.sex.value) !== 2) {
            dispatch({ type: ACTION_ACCESS, payload: { access: ACCESS_DENIED } })
            return
        }
        dispatch({ type: ACTION_ACCESS, payload: { access: ACCESS_LOADING } })
        try {
            const meta = await fetchMeta()
            dispatch({ type: ACTION_ACCESS, payload: { access: ACCESS_GRANTED, meta } })
        } catch(e) {
            dispatch({ type: ACTION_ACCESS, payload: { access: isNotFound(e) ? ACCESS_DENIED : null } })
        }
    }
}

export function loadMenuTotal() {
    return async dispatch => {
        try {
            const result = await updatesMore()
            if(result && result.escort_ads) {
                dispatch({ type: ACTION_MENU_TOTAL, payload: Number(result.escort_ads.total) || 0 })
            }
        } catch(e) { }
    }
}

export function receiveAds(list) {
    return {
        type: ACTION_RECEIVE_ADS,
        payload: list
    }
}

let listRequestId = 0

export function loadList(filters, reset) {
    return async (dispatch, getState) => {
        const key = filtersKey(filters)
        const current = getState().escortAds.list
        if(!reset && (!current || current.key !== key || current.loading || !current.hasMore)) {
            return
        }
        const offset = reset ? 0 : current.offset + PAGE_LIMIT
        // The next pages ask for the city and the order the server picked for the first one.
        const params = toParams(reset || !current.filters ? filters : current.filters)
        const requestId = ++listRequestId
        dispatch({ type: ACTION_LIST_REQUEST, payload: { key, reset, requestId } })
        try {
            const result = await browse(params, { offset, limit: PAGE_LIMIT })
            if(getState().escortAds.list.requestId !== requestId) {
                return
            }
            dispatch(receiveAds(result.list))
            dispatch(setHistoryTotal(result.history_total))
            dispatch({
                type: ACTION_LIST_SUCCESS,
                payload: {
                    ids: result.list.map(ad=>ad.id),
                    total: Number(result.total) || 0,
                    totalPhones: Number(result.total_phones) || 0,
                    filters: offset === 0 && result.filters ? {
                        ...filters,
                        city: result.filters.city || undefined,
                        order: result.filters.order || filters.order
                    } : null,
                    offset
                }
            })
        } catch(e) {
            if(getState().escortAds.list.requestId !== requestId) {
                return
            }
            dispatch({ type: ACTION_LIST_FAILURE })
            throw e
        }
    }
}

// Every answer that knows the count updates it. null means the answer did not count it.
export function setHistoryTotal(total) {
    return (dispatch, getState) => {
        if(total === null || total === undefined) {
            return
        }
        const value = Number(total) || 0
        if(getState().escortAds.historyTotal !== value) {
            dispatch({ type: ACTION_HISTORY_TOTAL, payload: value })
        }
    }
}

export function openHistory(type) {
    return {
        type: ACTION_HISTORY_DIALOG,
        payload: type === 'calls' ? 'calls' : 'views'
    }
}

export function closeHistory() {
    return {
        type: ACTION_HISTORY_DIALOG,
        payload: null
    }
}

export function saveListScroll(scrollY) {
    return {
        type: ACTION_LIST_SCROLL,
        payload: scrollY
    }
}

export function openPhoneDialog(id) {
    return {
        type: ACTION_PHONE_DIALOG,
        payload: id
    }
}

export function closePhoneDialog() {
    return {
        type: ACTION_PHONE_DIALOG,
        payload: null
    }
}

export function unlockPhone(id, purchase) {
    return async dispatch => {
        dispatch({ type: ACTION_PHONE_LOADING, payload: { id, loading: true } })
        try {
            const result = await unlockPhoneRequest(id, purchase)
            const patch = {
                phone: result.phone,
                phone_display: result.phone_display,
                phone_masked: result.phone_masked,
                phone_revealed: result.phone_revealed
            }
            dispatch({
                type: ACTION_PATCH_AD,
                payload: {
                    id,
                    patch
                }
            })
            if(result.phone_revealed) {
                dispatch({ type: ACTION_PHONE_UNLOCKED, payload: { id, patch } })
            }
            return result
        } finally {
            dispatch({ type: ACTION_PHONE_LOADING, payload: { id, loading: false } })
        }
    }
}

// Every call is counted before the phone app opens; the answer has the calls of the ad and the history count.
export function callAd(id) {
    return async dispatch => {
        const result = await callRequest(id)
        dispatch({
            type: ACTION_PATCH_AD,
            payload: {
                id,
                patch: {
                    calls: Number(result.calls) || 0
                }
            }
        })
        dispatch(setHistoryTotal(result.history_total))
        return result
    }
}

export function setFavorite(id, value) {
    return async dispatch => {
        const result = await favoriteRequest(id, value)
        dispatch({
            type: ACTION_PATCH_AD,
            payload: {
                id,
                patch: {
                    is_favorite: result.is_favorite
                }
            }
        })
        return result.is_favorite
    }
}

// An empty note deletes it.
export function saveNote(id, value) {
    return async dispatch => {
        const note = await noteRequest(id, value)
        dispatch({
            type: ACTION_PATCH_AD,
            payload: {
                id,
                patch: {
                    note: note || ''
                }
            }
        })
        return note
    }
}

export function reportAd(id, data) {
    return async dispatch => {
        const result = await reportRequest(id, data)
        dispatch({
            type: ACTION_PATCH_AD,
            payload: {
                id,
                patch: {
                    report_today: result.report_today ? true : false
                }
            }
        })
        return result
    }
}

export function saveReview(id, data) {
    return async dispatch => {
        const ad = await reviewRequest(id, data)
        const rating = ad.rating || {}
        dispatch({
            type: ACTION_PATCH_AD,
            payload: {
                id,
                patch: {
                    views_count: ad.views_count,
                    review_count: ad.review_count,
                    rating: ad.rating,
                    reviews: ad.reviews,
                    my_review: ad.my_review,
                    ...(ad.popularity ? { popularity: ad.popularity } : {}),
                    rating_score: rating.score,
                    rating_score_display: rating.score_display,
                    rating_label: rating.label
                }
            }
        })
        return ad
    }
}
